import React, { useState, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Circular Profile Image Component
 * - Displays profile image in a perfect circle
 * - Supports upload with camera icon overlay
 * - Auto-resizes and compresses images
 * - Mobile-friendly with touch support
 */
const CircularProfileImage = ({
  src,
  alt = 'Profile',
  size = 'lg',
  editable = false,
  onImageChange,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Size variants
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-40 h-40'
  };

  const iconSizes = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-4xl',
    xl: 'text-5xl'
  };

  const cameraButtonSizes = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
    xl: 'w-12 h-12 text-lg'
  };

  // Compress and resize image
  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Target size (max 500x500 for profile pics)
          const MAX_SIZE = 500;
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Draw with white background for transparency
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve({
                  blob,
                  dataUrl: canvas.toDataURL('image/jpeg', 0.85)
                });
              } else {
                reject(new Error('Failed to process image'));
              }
            },
            'image/jpeg',
            0.85 // 85% quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    try {
      setIsUploading(true);

      // Process image
      const { blob, dataUrl } = await processImage(file);

      // Set preview
      setPreviewUrl(dataUrl);

      // Call parent handler with processed image
      if (onImageChange) {
        await onImageChange({
          blob,
          dataUrl,
          fileName: file.name,
          mimeType: 'image/jpeg'
        });
      }

      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image');
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle click on the image/upload button
  const handleClick = () => {
    if (editable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine which image to show
  const displayImage = previewUrl || src;

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Hidden file input */}
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="profile-image-input"
        />
      )}

      {/* Profile Image Circle */}
      <div
        onClick={handleClick}
        className={`
          ${sizeClasses[size]}
          rounded-full
          overflow-hidden
          bg-emerald-100
          border-4 border-white
          shadow-lg
          flex items-center justify-center
          ${editable ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
          ${isUploading ? 'opacity-50' : ''}
        `}
        data-testid="profile-image-container"
      >
        {isUploading ? (
          // Loading state
          <div className="flex items-center justify-center w-full h-full bg-emerald-100">
            <i className={`fas fa-spinner fa-spin ${iconSizes[size]} text-emerald-600`}></i>
          </div>
        ) : displayImage ? (
          // Image
          <img
            src={displayImage}
            alt={alt}
            className="w-full h-full object-cover"
            data-testid="profile-image"
          />
        ) : (
          // Default placeholder
          <div className="flex items-center justify-center w-full h-full">
            <i className={`fas fa-user ${iconSizes[size]} text-emerald-600`} data-testid="profile-placeholder"></i>
          </div>
        )}
      </div>

      {/* Camera icon overlay for editable mode */}
      {editable && !isUploading && (
        <button
          onClick={handleClick}
          className={`
            absolute bottom-0 right-0
            ${cameraButtonSizes[size]}
            bg-emerald-600 hover:bg-emerald-700
            text-white
            rounded-full
            flex items-center justify-center
            shadow-md
            border-2 border-white
            transition-all duration-200
            hover:scale-110
            active:scale-95
          `}
          title="Upload profile picture"
          data-testid="upload-button"
        >
          <i className="fas fa-camera"></i>
        </button>
      )}
    </div>
  );
};

export default CircularProfileImage;
